import React, { useState, useEffect, useCallback, useRef } from 'react';
import { attritionApi } from '../lib/api';
import { useUnit } from '../contexts/UnitContext';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { HR_MONTHS, currentFy } from '../lib/utils';

const MONTH_FULL = { Apr: 'April', May: 'May', Jun: 'June', Jul: 'July', Aug: 'August', Sep: 'September',
  Oct: 'October', Nov: 'November', Dec: 'December', Jan: 'January', Feb: 'February', Mar: 'March' };

// 'Apr' of FY 2026-27 -> '2026-04'. Jan..Mar belong to the next calendar year.
const ymOf = (fy, m) => {
  const start = parseInt(fy.slice(0, 4), 10);
  const idx = HR_MONTHS.indexOf(m);
  const y = idx <= 8 ? start : start + 1;
  return `${y}-${String(idx <= 8 ? idx + 4 : idx - 8).padStart(2, '0')}`;
};

export default function Attrition() {
  // These registers are per plant: one unit's entries must never
  // appear on, or score against, another.
  const { activeUnit } = useUnit();
  const [data, setData] = useState(null);
  const [fySel, setFySel] = useState(currentFy());

  // A plant switch leaves the previous read in flight; without this guard the
  // slower reply wins and the register shows another plant's months.
  const wanted = useRef(activeUnit);
  const load = useCallback(async () => {
    wanted.current = activeUnit;
    try {
      const r = await attritionApi.list(activeUnit);
      if (wanted.current === activeUnit) setData(r.data);
    }
    catch (e) { if (wanted.current === activeUnit) toast.error('Failed to load the attrition register'); }
  }, [activeUnit]);
  useEffect(() => { load(); }, [load]);

  // There is deliberately no save or clear here any more. Attrition is a
  // measurement of what the employee records already say, so there is nothing
  // for anyone to type: a figure entered by hand could only ever disagree with
  // the exits it is supposed to describe. The register's API is left in place
  // and untouched for the historical rows it already holds.

  const fys = [...new Set([currentFy(), ...(data?.rows || []).map((r) => r.fy)])].sort().reverse();
  const byYm = Object.fromEntries((data?.rows || []).map((r) => [r.ym, r]));
  // Calculated by the server from the employee records — leavers that month over
  // headcount that month. Never typed, so it cannot disagree with the KPI, which
  // scores from the same function.
  const autoByYm = Object.fromEntries((data?.auto_attrition || []).map((a) => [a.ym, a]));
  const months = HR_MONTHS.map((m) => {
    const ym = ymOf(fySel, m);
    return { m, ym, r: byYm[ym], a: autoByYm[ym] };
  });
  // Only months the employee records actually cover. A month with no record
  // behind it stays blank rather than being shown as a calculated zero.
  const known = months.filter((x) => x.a);
  const sumLeavers = known.reduce((s, x) => s + Number(x.a.leavers || 0), 0);
  // The year's rate is the year's leavers over the year's average roll — NOT
  // the mean of twelve monthly percentages, which would weight a month with 3
  // people on the books the same as one with 90.
  const avgHead = known.length
    ? known.reduce((s, x) => s + Number(x.a.headcount || 0), 0) / known.length : 0;
  const fyPct = avgHead > 0 ? Math.round((sumLeavers / avgHead) * 100 * 100) / 100 : 0;

  // ---- Associates -----------------------------------------------------------
  // Derived from the Associates register as a roll-forward — the roll the
  // month opened with, plus who was added, minus who left — for every month
  // the register can reach. Months before it was stocked have no history to
  // recompute and keep whatever HR typed, so each row says which it is.
  //
  // Staff is untouched by any of this: it is derived above from the employee
  // records and this view never writes to it.
  const [view, setView] = useState('staff');
  const [draft, setDraft] = useState({});          // ym -> { headcount, left }
  const [saving, setSaving] = useState('');
  const assocByYm = Object.fromEntries((data?.associates || []).map((a) => [a.ym, a]));
  const live = data?.associates_live;
  const firstDerived = data?.associates_first_derived_month;

  // The percentage is NEVER typed. Shown live from whatever is in the two
  // boxes, so the number on screen always matches the counts beside it.
  const pctOf = (head, left) => {
    const h = Number(head); const l = Number(left);
    if (!Number.isFinite(h) || h <= 0 || !Number.isFinite(l) || l < 0) return 0;
    return Math.round((l / h) * 100 * 100) / 100;
  };
  const cellOf = (ym, field) => {
    if (draft[ym] && draft[ym][field] !== undefined) return draft[ym][field];
    const a = assocByYm[ym];
    return a ? String(a[field === 'headcount' ? 'headcount' : 'left']) : '';
  };
  // A month the register accounts for is read-only, for the same reason Staff
  // is: a typed number could only ever disagree with the joiners and exits
  // behind it. The server refuses the write too.
  //
  // Months still in the future count as derived even though no row exists for
  // them yet — the register will answer for them when they arrive, and the
  // server already refuses to let one be typed. Offering an input the save
  // would bounce is worse than showing nothing.
  const isDerived = (ym) => assocByYm[ym]?.source === 'register'
    || (!!firstDerived && ym >= firstDerived);
  const setCell = (ym, field, v) =>
    setDraft((d) => ({ ...d, [ym]: { ...(d[ym] || {}), [field]: v.replace(/[^0-9]/g, '') } }));

  const saveAssoc = async (ym) => {
    const head = Number(cellOf(ym, 'headcount') || 0);
    const left = Number(cellOf(ym, 'left') || 0);
    if (left > head) { toast.error(`Associates who left (${left}) cannot be more than the headcount (${head}).`); return; }
    setSaving(ym);
    try {
      await attritionApi.saveAssociates({ ym, unit: activeUnit || '', headcount: head, left });
      toast.success('Associates saved');
      setDraft((d) => { const n = { ...d }; delete n[ym]; return n; });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save'); }
    finally { setSaving(''); }
  };

  return (
    <div className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.02em] text-[#172554]">Attrition</h1>
            <p className="text-sm text-[#5A6377]">
              Calculated from the employee records — nothing here is typed. A month's rate is the
              people whose <b>last working day</b> fell in that month, over the headcount on the roll
              that month. Somebody serving notice counts in the month they actually leave, not when
              they resigned, so the figure moves on its own as each exit completes.
            </p>
          </div>

          {/* Staff is calculated, Associates is counted by hand. Two separate
              registers behind one switch, never one register with a mode. */}
          <div className="inline-flex rounded-lg border border-[#E6E9F0] bg-white p-1" role="tablist">
            {[['staff', 'Staff'], ['associates', 'Associates']].map(([k, label]) => (
              <button key={k} role="tab" aria-selected={view === k}
                data-testid={`attrition-view-${k}`}
                onClick={() => setView(k)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  view === k ? 'bg-[#2563EB] text-white' : 'text-[#5A6377] hover:bg-[#F2F4F8]'}`}>
                {label}
              </button>
            ))}
          </div>

          {view === 'staff' && (
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-medium text-[#11182B]">Monthly register — Staff</h3>
              <select value={fySel} onChange={(e) => setFySel(e.target.value)}
                data-testid="attrition-fy"
                className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-sm">
                {fys.map((f) => <option key={f} value={f}>FY {f}</option>)}
              </select>
              {!data && <span className="text-[#98A1B2] text-sm">Loading…</span>}
              <span className="text-xs text-[#15654B] ml-auto font-medium"
                data-testid="attrition-auto-badge">
                Auto calculated from employee exit records
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-[#C9CFDC]" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="bg-[#EFF1F6] text-[#11182B] text-xs uppercase">
                    <th className="py-2 px-3 text-left border border-[#C9CFDC]">Month</th>
                    <th className="py-2 px-3 text-right border border-[#C9CFDC]">Attrition Staff</th>
                    <th className="py-2 px-3 text-right border border-[#C9CFDC]">Headcount</th>
                    <th className="py-2 px-3 text-right border border-[#C9CFDC]">Attrition&nbsp;%</th>
                  </tr>
                </thead>
                <tbody>
                  {months.map(({ m, ym, a }) => (
                    <tr key={ym}>
                      <td className="py-1.5 px-3 border border-[#E6E9F0] font-medium text-[#11182B]">
                        {MONTH_FULL[m]}
                      </td>
                      {/* One exits column. The worker column was dropped on
                          request, and with it the Total Exits column it would
                          have duplicated — there is no staff/worker flag on an
                          employee record, so every exit counts here. */}
                      <td className="py-1.5 px-3 border border-[#E6E9F0] text-right font-semibold text-[#172554]"
                        data-testid={`attrition-staff-${ym}`}>
                        {a ? a.leavers : '—'}
                      </td>
                      <td className="py-1.5 px-3 border border-[#E6E9F0] text-right text-[#5A6377]"
                        data-testid={`attrition-headcount-${ym}`}>
                        {a ? a.headcount : '—'}
                      </td>
                      <td className="py-1.5 px-3 border border-[#E6E9F0] text-right font-semibold text-[#172554]"
                        data-testid={`attrition-pct-${ym}`}>
                        {a ? `${a.attrition_pct.toFixed(2)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-[#F7F8FB] font-bold text-[#172554]">
                    <td className="py-2 px-3 border border-[#C9CFDC]">Total</td>
                    <td className="py-2 px-3 border border-[#C9CFDC] text-right"
                      data-testid="attrition-total-leavers">{known.length ? sumLeavers : '—'}</td>
                    <td className="py-2 px-3 border border-[#C9CFDC] text-right"></td>
                    <td className="py-2 px-3 border border-[#C9CFDC] text-right"
                      data-testid="attrition-fy-pct">{known.length ? `${fyPct.toFixed(2)}%` : '—'}</td>
                  </tr>
                  <tr className="bg-[#F7F8FB] text-[#5A6377]">
                    <td className="py-2 px-3 border border-[#C9CFDC]">Avg. headcount</td>
                    <td className="py-2 px-3 border border-[#C9CFDC] text-right"></td>
                    <td className="py-2 px-3 border border-[#C9CFDC] text-right">
                      {known.length ? avgHead.toFixed(1) : '—'}</td>
                    <td className="py-2 px-3 border border-[#C9CFDC] text-right"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-[#98A1B2] mt-2">
              The year's rate is the year's exits over the average monthly headcount, not the mean of
              twelve percentages — that would weight a month with 3 people on the books the same as one
              with 90. A month with no employee records behind it is left blank rather than shown as a
              calculated 0%.
            </p>
            <p className="text-[11px] text-[#15654B] mt-1" data-testid="attrition-staff-note">
              Staff figures are read from the employee records and cannot be edited here — a typed
              number could only ever disagree with the exits it describes. Associates work the same
              way on the other tab, off the Associates register.
            </p>
          </div>
          )}

          {view === 'associates' && (
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <h3 className="font-medium text-[#11182B]">Monthly register — Associates</h3>
              <span className="text-xs text-[#15654B] ml-auto font-medium"
                data-testid="attrition-manual-badge">
                {firstDerived
                  ? `Calculated from the Associates register from ${firstDerived} · earlier months typed`
                  : 'No associates on the register yet · every month typed'}
              </span>
            </div>

            {/* The month in progress. Shown even before the register can score
                a full month, so a register that is quietly working is visible
                rather than looking like nothing happened. */}
            {live && (
              <div className="flex flex-wrap gap-2 mb-4" data-testid="assoc-live-strip">
                {[['On the roll now', live.closing], ['Opened with', live.opening],
                  ['Added', live.joined], ['Left', live.left]].map(([label, n]) => (
                  <div key={label} className="bg-[#F7FAFF] border border-[#E6E9F0] rounded-lg px-3 py-2 text-sm">
                    <b className="text-[#172554]">{n}</b> <span className="text-[#5A6377]">{label}</span>
                  </div>
                ))}
                <div className="text-[11px] text-[#98A1B2] self-center ml-1">
                  {live.derivable
                    ? 'this month so far — moves as the register does'
                    : `since the register was stocked on ${data.associates_register_start} — `
                      + `this month cannot be scored because its opening roll predates the register`}
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-[#C9CFDC]" style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr className="bg-[#EFF1F6] text-[#11182B] text-xs uppercase">
                    <th className="py-2 px-3 text-left border border-[#C9CFDC]">Month</th>
                    <th className="py-2 px-3 text-right border border-[#C9CFDC]"
                        title="The roll the month opened with — the base attrition is measured against">
                      Opening
                    </th>
                    <th className="py-2 px-3 text-right border border-[#C9CFDC]">Added</th>
                    <th className="py-2 px-3 text-right border border-[#C9CFDC]">Left</th>
                    <th className="py-2 px-3 text-right border border-[#C9CFDC]">Closing</th>
                    <th className="py-2 px-3 text-right border border-[#C9CFDC]">Attrition&nbsp;%</th>
                    <th className="py-2 px-3 border border-[#C9CFDC]"></th>
                  </tr>
                </thead>
                <tbody>
                  {months.map(({ m, ym }) => {
                    const a = assocByYm[ym];
                    const derived = isDerived(ym);

                    // Derivable but not here yet — a month that has not begun.
                    if (derived && !a) {
                      return (
                        <tr key={ym} className="bg-[#FAFCFF] text-[#C9CFDC]" data-testid={`assoc-row-${ym}`}>
                          <td className="py-1.5 px-3 border border-[#E6E9F0] font-medium text-[#98A1B2]">
                            {MONTH_FULL[m]}
                            <span className="ml-2 text-[10px] uppercase tracking-wide"
                              data-testid={`assoc-src-${ym}`}>awaiting</span>
                          </td>
                          {[0, 1, 2, 3, 4, 5].map((i) => (
                            <td key={i} className="py-1.5 px-3 border border-[#E6E9F0] text-right">—</td>
                          ))}
                        </tr>
                      );
                    }

                    // Calculated months are read straight through — no inputs,
                    // nothing to save, nothing that can contradict the register.
                    if (derived) {
                      return (
                        <tr key={ym} className="bg-[#FAFCFF]" data-testid={`assoc-row-${ym}`}>
                          <td className="py-1.5 px-3 border border-[#E6E9F0] font-medium text-[#11182B]">
                            {MONTH_FULL[m]}
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-[#15654B]"
                              data-testid={`assoc-src-${ym}`}>calculated</span>
                          </td>
                          <td className="py-1.5 px-3 border border-[#E6E9F0] text-right tabular-nums">{a.opening}</td>
                          <td className="py-1.5 px-3 border border-[#E6E9F0] text-right tabular-nums text-[#15654B]">
                            {a.joined ? `+${a.joined}` : '0'}
                          </td>
                          <td className="py-1.5 px-3 border border-[#E6E9F0] text-right tabular-nums text-[#B42318]">
                            {a.left ? `−${a.left}` : '0'}
                          </td>
                          <td className="py-1.5 px-3 border border-[#E6E9F0] text-right tabular-nums font-medium">{a.closing}</td>
                          <td className="py-1.5 px-3 border border-[#E6E9F0] text-right font-semibold text-[#172554]"
                            data-testid={`assoc-pct-${ym}`}>
                            {/* No opening roll means nothing to be a percentage
                                OF — reported as unknown, never as a clean 0%. */}
                            {a.unscored ? '—' : `${a.attrition_pct.toFixed(2)}%`}
                          </td>
                          <td className="py-1 px-2 border border-[#E6E9F0]" />
                        </tr>
                      );
                    }

                    // Before the register existed: the figure HR typed is the
                    // only record there is, so it stays editable.
                    const left = cellOf(ym, 'left');
                    const head = cellOf(ym, 'headcount');
                    const dirty = !!draft[ym];
                    const bad = Number(left || 0) > Number(head || 0);
                    return (
                      <tr key={ym} data-testid={`assoc-row-${ym}`}>
                        <td className="py-1.5 px-3 border border-[#E6E9F0] font-medium text-[#11182B]">
                          {MONTH_FULL[m]}
                          {a && (
                            <span className="ml-2 text-[10px] uppercase tracking-wide text-[#8A5A12]"
                              data-testid={`assoc-src-${ym}`}>typed</span>
                          )}
                        </td>
                        <td className="py-1 px-2 border border-[#E6E9F0] text-right">
                          <input inputMode="numeric" value={head}
                            data-testid={`assoc-head-${ym}`}
                            onChange={(e) => setCell(ym, 'headcount', e.target.value)}
                            className="w-20 text-right border border-[#E6E9F0] rounded px-2 py-1" />
                        </td>
                        {/* The register cannot say who came and went in a month
                            it was not keeping, so these stay blank rather than
                            implying a movement of zero. */}
                        <td className="py-1.5 px-3 border border-[#E6E9F0] text-right text-[#C9CFDC]">—</td>
                        <td className="py-1 px-2 border border-[#E6E9F0] text-right">
                          <input inputMode="numeric" value={left}
                            data-testid={`assoc-left-${ym}`}
                            onChange={(e) => setCell(ym, 'left', e.target.value)}
                            className={`w-20 text-right border rounded px-2 py-1 ${
                              bad ? 'border-[#E11D2A] bg-[#FDF2F3]' : 'border-[#E6E9F0]'}`} />
                        </td>
                        <td className="py-1.5 px-3 border border-[#E6E9F0] text-right text-[#C9CFDC]">—</td>
                        <td className="py-1.5 px-3 border border-[#E6E9F0] text-right font-semibold text-[#172554]"
                          data-testid={`assoc-pct-${ym}`}>
                          {head === '' && left === '' ? '—' : `${pctOf(head, left).toFixed(2)}%`}
                        </td>
                        <td className="py-1 px-2 border border-[#E6E9F0] text-right">
                          {dirty && (
                            <button onClick={() => saveAssoc(ym)} disabled={saving === ym || bad}
                              data-testid={`assoc-save-${ym}`}
                              className="text-xs px-2.5 py-1 rounded-md bg-[#2563EB] text-white disabled:opacity-50">
                              {saving === ym ? 'Saving…' : 'Save'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-[#98A1B2] mt-2">
              {data?.associates_note}
              {' '}Attrition is the exits over the <b>opening</b> roll, not the closing one — a month
              of heavy hiring would otherwise make the same number of exits look like a better month.
              Adding or exiting somebody on the Associates screen moves these figures on its own.
            </p>
          </div>
          )}
        </main>
      </div>
    </div>
  );
}
